import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  Text,
  Button,
  Input,
  makeStyles,
  tokens,
  Avatar,
  Combobox,
  Option,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Checkbox,
  Spinner,
} from '@fluentui/react-components';
import { Add24Regular, Edit24Regular, Delete24Regular, MoreHorizontal24Regular, ChevronDown24Regular, ChevronRight24Regular, Share24Regular, Archive24Regular, Filter24Regular, Settings24Regular } from '@fluentui/react-icons';
import { KanbanBoard as KanbanBoardType, KanbanCard, KanbanUser, CreateCardRequest, MoveCardRequest, BoardInfo } from '../../types/kanban';
import { TeamsFxContext } from '../Context';
import { KanbanServiceV2 } from '../../services/kanbanServiceV2';
import { notificationService } from '../../services/notificationService';
import { LicenseCheck } from '../../types/license';
import { checkLicense, getFreeTierDefaults, canAddTask, clearLicenseCache } from '../../services/licenseService';

// Import refactored components
import { assignAvatarColors, getUserAvatarColor, AvatarColor } from './avatarUtils';
import { CARD_COLORS } from './types';
import { ReadOnlyBanner, LicenseLimitBanner } from './LicenseBanner';
import { BoardSelector } from './BoardSelector';
import { TaskCard } from './TaskCard';
import { CreateTaskDialog } from './CreateTaskDialog';
import { EditTaskDialog } from './EditTaskDialog';
import { ArchivedTasksDialog } from './ArchivedTasksDialog';
import {
  DeleteColumnDialog,
  DeleteRowDialog,
  DeleteTaskDialog,
  AddColumnDialog,
  AddRowDialog,
  ArchiveConfirmDialog,
  ErrorDialog,
} from './ConfirmDialogs';
import {
  CreateBoardDialog,
  RenameBoardDialog,
  DeleteBoardDialog,
  ColumnMoveDialog,
  DeleteCardsDialog,
} from './BoardDialogs';

const useStyles = makeStyles({
  centeredDialog: {
    '& > div': {
      alignItems: 'center',
    },
  },
  themedScrollbar: {
    '&::-webkit-scrollbar': {
      width: '12px',
      height: '12px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: tokens.colorNeutralBackground3,
      borderRadius: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralForeground4,
      borderRadius: '6px',
      border: `2px solid ${tokens.colorNeutralBackground3}`,
      '&:hover': {
        backgroundColor: tokens.colorNeutralForeground3,
      },
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${tokens.colorNeutralForeground4} ${tokens.colorNeutralBackground3}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    gap: '1.5rem',
    boxSizing: 'border-box',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '2rem',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
  },
  loadingTitle: {
    color: tokens.colorNeutralForeground1,
    marginTop: '0.5rem',
  },
  loadingSubtitle: {
    color: tokens.colorNeutralForeground3,
  },
  board: {
    padding: '2rem',
    marginRight: '2px',
    marginBottom: '2px',
    overflowX: 'auto',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    boxSizing: 'border-box',
    WebkitOverflowScrolling: 'touch' as any,
    '@media (max-width: 640px)': {
      padding: '0.75rem',
      width: '100vw',
      maxWidth: '100vw',
    },
  },
  boardHeader: {
    display: 'flex',
    gap: '1.5rem',
    marginBottom: '2rem',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: tokens.shadow4,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    '@media (max-width: 640px)': {
      padding: '0.75rem',
      gap: '0.5rem',
      marginBottom: '1rem',
      flexWrap: 'wrap',
    },
  },
  swimlaneContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    width: 'max-content',
    minWidth: '100%',
  },
  swimlane: {
    marginBottom: '2rem',
    '&:last-child': {
      marginBottom: '1rem',
    },
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.25rem',
    position: 'relative',
    zIndex: 1,
  },
  rowTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground2,
    textTransform: 'none',
    letterSpacing: 'normal',
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  rowSeparator: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    marginLeft: '1rem',
  },
  collapseButton: {
    minWidth: 'auto',
    padding: '4px',
    color: tokens.colorNeutralForeground3,
    '&:hover': {
      color: tokens.colorNeutralForeground2,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  columnCells: {
    display: 'flex',
    gap: '1.5rem',
    marginLeft: '1rem',
    width: 'max-content',
    minWidth: '100%',
  },
  columnCell: {
    flex: '1 1 280px',
    minWidth: '280px',
    maxWidth: '280px',
    minHeight: '120px',
    backgroundColor: 'transparent',
    padding: '0',
    margin: '0',
    '@media (max-width: 640px)': {
      flex: '1 1 240px',
      minWidth: '240px',
      maxWidth: '240px',
    },
  },
  columnHeaderCell: {
    flex: '1 1 280px',
    minWidth: '280px',
    maxWidth: '280px',
    '@media (max-width: 640px)': {
      flex: '1 1 240px',
      minWidth: '240px',
      maxWidth: '240px',
    },
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    boxShadow: tokens.shadow2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    marginBottom: '0.25rem',
    transition: 'all 0.2s ease',
    margin: '0',
  },
  columnTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '0.5rem',
  },
  columnHeaderContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '1rem',
  },
  cardList: {
    minHeight: '400px',
  },
  card: {
    marginBottom: '1rem',
    cursor: 'pointer',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-1px)',
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  cardMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priorityBadge: {
    fontSize: '12px',
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  assignees: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  formField: {
    marginBottom: tokens.spacingVerticalM,
  },
  assignmentPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  assignmentPill: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
  },
  dismissButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXXS,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  assignmentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  addColumnButton: {
    height: '60px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    transition: 'all 0.2s ease',
    boxShadow: tokens.shadow2,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `2px dashed ${tokens.colorNeutralStroke1}`,
      color: tokens.colorNeutralForeground1,
      boxShadow: tokens.shadow4,
      transform: 'translateY(-1px)',
    },
  },
  cardDescription: {
    fontSize: '14px',
    lineHeight: '18px',
    margin: 0,
    padding: 0,
    width: '100%',
    display: '-webkit-box !important',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    color: tokens.colorNeutralForeground1,
  },
  colorPicker: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  colorOption: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusSmall,
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorOptionSelected: {
    border: `3px solid ${tokens.colorBrandBackground}`,
  },
  rowContainer: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    marginBottom: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  rowContent: {
    padding: tokens.spacingVerticalS,
    minHeight: '60px',
    borderBottomLeftRadius: tokens.borderRadiusSmall,
    borderBottomRightRadius: tokens.borderRadiusSmall,
  },
  cardDeleteButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 2,
    backgroundColor: 'transparent',
    borderRadius: '4px',
    padding: '4px',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dateInput: {
    '& input[type="date"]::-webkit-datetime-edit-text': {
      color: tokens.colorNeutralForeground3,
    },
    '& input[type="date"]::-webkit-datetime-edit-month-field': {
      color: tokens.colorNeutralForeground3,
    },
    '& input[type="date"]::-webkit-datetime-edit-day-field': {
      color: tokens.colorNeutralForeground3,
    },
    '& input[type="date"]::-webkit-datetime-edit-year-field': {
      color: tokens.colorNeutralForeground3,
    },
    '& input[type="date"][data-has-value="true"]::-webkit-datetime-edit-text': {
      color: tokens.colorNeutralForeground1,
    },
    '& input[type="date"][data-has-value="true"]::-webkit-datetime-edit-month-field': {
      color: tokens.colorNeutralForeground1,
    },
    '& input[type="date"][data-has-value="true"]::-webkit-datetime-edit-day-field': {
      color: tokens.colorNeutralForeground1,
    },
    '& input[type="date"][data-has-value="true"]::-webkit-datetime-edit-year-field': {
      color: tokens.colorNeutralForeground1,
    },
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(0.5)',
      cursor: 'pointer',
      opacity: 1,
    },
    '@media (prefers-contrast: more)': {
      '& input[type="date"]::-webkit-calendar-picker-indicator': {
        filter: 'invert(1)',
        opacity: 1,
      },
    },
    '@media (prefers-color-scheme: dark)': {
      '& input[type="date"]::-webkit-calendar-picker-indicator': {
        filter: 'invert(1)',
      },
    },
  },
  commentContainer: {
    transition: 'background-color 0.6s ease-out, transform 0.6s ease-out, box-shadow 0.6s ease-out',
  },
});

interface KanbanBoardProps {
  boardId?: string;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({ boardId }) => {
  const styles = useStyles();
  const isMobile = useMemo(() => window.matchMedia('(max-width: 640px)').matches, []);

  // Card color options
  // Use CARD_COLORS imported from types.ts
  const cardColors = CARD_COLORS;

  const [board, setBoard] = useState<KanbanBoardType | null>(null);
  const [availableMembers, setAvailableMembers] = useState<KanbanUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isCreateCardDialogOpen, setIsCreateCardDialogOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<string>('');
  const [newCard, setNewCard] = useState<Partial<CreateCardRequest>>({
    title: '',
    description: '',
    priority: 'medium',
    assignedTo: [],
    color: '#2196F3', // Default to blue
    startDate: '',
    endDate: '',
  });
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isEditCardDialogOpen, setIsEditCardDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<any>(null);
  const [userPhotos, setUserPhotos] = useState<Record<string, string>>({});
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editingColumnTitle, setEditingColumnTitle] = useState<string>('');
  const [isAddColumnDialogOpen, setIsAddColumnDialogOpen] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState<string>('');
  const [isDeleteColumnDialogOpen, setIsDeleteColumnDialogOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null);
  const [isDeleteErrorDialogOpen, setIsDeleteErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string>('');
  const [isCreateRowDialogOpen, setIsCreateRowDialogOpen] = useState(false);
  const [newRowName, setNewRowName] = useState<string>('');
  const [selectedRowForCard, setSelectedRowForCard] = useState<string>('');
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editingRowName, setEditingRowName] = useState<string>('');
  const [selectedUserFilters, setSelectedUserFilters] = useState<Set<string>>(new Set());
  const [selectedPriorityFilters, setSelectedPriorityFilters] = useState<Set<string>>(new Set());
  const [selectedColorFilters, setSelectedColorFilters] = useState<Set<string>>(new Set());
  const [showPastDueOnly, setShowPastDueOnly] = useState(false);
  const [isDeleteRowDialogOpen, setIsDeleteRowDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set());
  const [isDeleteCardDialogOpen, setIsDeleteCardDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<any>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isColumnMoveConfirmOpen, setIsColumnMoveConfirmOpen] = useState(false);
  const [pendingColumnMove, setPendingColumnMove] = useState<{
    sourceIndex: number;
    destinationIndex: number;
    columnTitle: string;
  } | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [archivedCards, setArchivedCards] = useState<any[]>([]);
  const [archiveSearchQuery, setArchiveSearchQuery] = useState('');
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [archiveConfirmData, setArchiveConfirmData] = useState<{ type: 'column' | 'row'; id: string; count: number; name: string } | null>(null);
  const [isDeleteCardsConfirmOpen, setIsDeleteCardsConfirmOpen] = useState(false);
  const [deleteCardsConfirmData, setDeleteCardsConfirmData] = useState<{ type: 'column' | 'row'; id: string; count: number; name: string } | null>(null);
  const [newComment, setNewComment] = useState('');
  const [currentUser, setCurrentUser] = useState<KanbanUser | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [newlyAddedCommentId, setNewlyAddedCommentId] = useState<string | null>(null);
  const commentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const commentsContainerRef = useRef<HTMLDivElement | null>(null);
  const commentTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [customUserInput, setCustomUserInput] = useState('');
  const [license, setLicense] = useState<LicenseCheck | null>(null);
  const [memberPermissionWarning, setMemberPermissionWarning] = useState(false);

  // Board management state
  const [boardList, setBoardList] = useState<BoardInfo[]>([]);
  const [contextPrefix, setContextPrefix] = useState<string>('');
  const [isCreateBoardDialogOpen, setIsCreateBoardDialogOpen] = useState(false);
  const [isRenameBoardDialogOpen, setIsRenameBoardDialogOpen] = useState(false);
  const [isDeleteBoardDialogOpen, setIsDeleteBoardDialogOpen] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [editingBoardTitle, setEditingBoardTitle] = useState('');
  const [pendingNotifications, setPendingNotifications] = useState<Set<string>>(new Set());
  const [deepLinkCardId, setDeepLinkCardId] = useState<string | null>(null);
  const [originalCardSnapshot, setOriginalCardSnapshot] = useState<{
    title: string;
    description: string;
    priority: string;
    assignedTo: string[];
    color: string;
    startDate: string;
    endDate: string;
  } | null>(null);

  const { platformContext } = useContext(TeamsFxContext);
  if (!platformContext) {
    throw new Error('KanbanBoard requires a platformContext in TeamsFxContext');
  }

  // Open pricing page for new purchases, or Stripe Customer Portal for renewals
  const openPricingPage = async () => {
    const context = platformContext.getContext();

    // For renewals with existing Stripe customer, use Customer Portal
    if (license?.isExpired && license?.stripeCustomerId && license?.plan !== 'free') {
      try {
        const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://kanbanprojecttracker.com';
        const apiEndpoint = process.env.REACT_APP_FUNC_ENDPOINT || 'https://api.kanbanprojecttracker.com';
        const response = await fetch(`${apiEndpoint}/stripe/portal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: license.stripeCustomerId,
            returnUrl: `${marketingUrl}/success`,
          }),
        });
        const data = await response.json();
        if (data.url) {
          window.open(data.url, '_blank');
          return;
        }
      } catch (error) {
        console.error('Failed to create portal session:', error);
        // Fall through to pricing page
      }
    }

    // New purchases go to pricing page
    const params = new URLSearchParams();
    if (currentUser?.email) params.set('userEmail', currentUser.email);
    if (context?.tenantId) params.set('tenantId', context.tenantId);
    const marketingBaseUrl = process.env.REACT_APP_MARKETING_URL || 'https://kanbanprojecttracker.com';
    const pricingUrl = `${marketingBaseUrl}/pricing?${params.toString()}`;
    window.open(pricingUrl, '_blank');
  };

  // Reload license info
  const loadLicenseInfo = async () => {
    const context = platformContext.getContext();
    const taskCount = board?.cards?.length || 0;
    const licenseResult = await checkLicense(currentUser?.email, context?.tenantId, taskCount);
    setLicense(licenseResult);
  };

  // Compute avatar colors ensuring users with same initials get different colors
  const avatarColorMap = useMemo(() => {
    // Collect all users from availableMembers and card assignees
    const allUsers = new Map<string, {id: string, displayName: string}>();

    availableMembers.forEach(member => {
      allUsers.set(member.id, { id: member.id, displayName: member.displayName });
    });

    // Also include users from card assignments (they may not be in availableMembers)
    board?.cards?.forEach(card => {
      card.assignedTo?.forEach(user => {
        if (!allUsers.has(user.id)) {
          allUsers.set(user.id, { id: user.id, displayName: user.displayName });
        }
      });
      // Include comment authors too
      card.comments?.forEach(comment => {
        if (!allUsers.has(comment.author.id)) {
          allUsers.set(comment.author.id, { id: comment.author.id, displayName: comment.author.displayName });
        }
      });
    });

    return assignAvatarColors(Array.from(allUsers.values()));
  }, [availableMembers, board?.cards]);

  // Helper to get avatar color with fallback
  const getAvatarColor = (userId: string): AvatarColor => {
    return avatarColorMap.get(userId) || getUserAvatarColor(userId);
  };

  // Photo cache constants
  const PHOTO_CACHE_PREFIX = 'user-photo-';
  const PHOTO_CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // Get cached photo from localStorage
  const getCachedPhoto = (userId: string): { photoData: string | null; isStale: boolean } | null => {
    try {
      const cached = localStorage.getItem(`${PHOTO_CACHE_PREFIX}${userId}`);
      if (!cached) return null;

      const { photoData, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;
      const isStale = age > PHOTO_CACHE_MAX_AGE;

      return { photoData, isStale };
    } catch {
      return null;
    }
  };

  // Save photo to localStorage cache
  const cachePhoto = (userId: string, photoData: string | null) => {
    try {
      localStorage.setItem(`${PHOTO_CACHE_PREFIX}${userId}`, JSON.stringify({
        photoData,
        timestamp: Date.now(),
      }));
    } catch (error) {
      // localStorage might be full - silently fail
      console.warn('Failed to cache photo:', error);
    }
  };

  // Fetch photo from API
  const fetchPhotoFromApi = async (userId: string, token: string): Promise<string | null> => {
    try {
      const apiEndpoint = process.env.REACT_APP_FUNC_ENDPOINT || '';
      const photoUrl = `${apiEndpoint}/users/${userId}/photo`;

      const response = await fetch(photoUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.photoData) {
          return data.photoData;
        }
      }

      return null;
    } catch (error) {
      console.warn(`Failed to fetch photo for user ${userId}:`, error);
      return null;
    }
  };

  // Function to load user photo with caching
  const loadUserPhoto = async (userId: string, token: string): Promise<string | null> => {
    if (!token) {
      return null;
    }

    // Skip custom users - they don't have photos in Graph
    if (userId.startsWith('custom-')) {
      return null;
    }

    // Check cache first
    const cached = getCachedPhoto(userId);
    if (cached && !cached.isStale) {
      // Fresh cache hit - return immediately
      return cached.photoData;
    }

    if (cached && cached.isStale) {
      // Stale cache - return cached version immediately, refresh in background
      fetchPhotoFromApi(userId, token).then(freshPhoto => {
        if (freshPhoto !== cached.photoData) {
          // Photo changed - update cache and state
          cachePhoto(userId, freshPhoto);
          if (freshPhoto) {
            setUserPhotos(prev => ({ ...prev, [userId]: freshPhoto }));
          }
        } else {
          // Photo unchanged - just update cache timestamp
          cachePhoto(userId, cached.photoData);
        }
      });
      return cached.photoData;
    }

    // No cache - fetch from API
    const photoData = await fetchPhotoFromApi(userId, token);
    cachePhoto(userId, photoData);
    return photoData;
  };

  // Note: No cleanup needed - photos are now base64 data URLs, not blob URLs
  const kanbanService = KanbanServiceV2.getInstance();

  useEffect(() => {
    const initializeBoard = async () => {
      try {
        // Check if we're in read-only mode from URL params
        const urlParams = new URLSearchParams(window.location.search);
        const readonlyParam = urlParams.get('readonly');
        const boardIdParam = urlParams.get('boardId');

        if (readonlyParam === 'true') {
          setIsReadOnly(true);
        }

        // Initialize Teams (handles standalone mode internally) - fast
        await platformContext.initialize();

        // Get context and prefix - fast (synchronous after init)
        const context = platformContext.getContext();
        const prefix = platformContext.getContextPrefix();
        setContextPrefix(prefix);

        // Load board from API FIRST - this is fast!
        let kanbanBoard = null;

        if (boardIdParam || boardId) {
          const effectiveBoardId = boardIdParam || boardId;
          console.log('KanbanBoard: Loading specific board ID:', effectiveBoardId);
          kanbanBoard = await kanbanService.getBoard(effectiveBoardId!);
        } else {
          console.log('KanbanBoard: Loading boards for context prefix:', prefix);
          const existingBoards = await kanbanService.listBoards(prefix);
          setBoardList(existingBoards);

          if (existingBoards.length > 0) {
            // Prefer the last viewed board for this context, fall back to most recently updated
            const lastViewedKey = `kanban_lastBoard_${prefix}`;
            const lastViewedId = localStorage.getItem(lastViewedKey);
            const lastViewedBoard = lastViewedId ? existingBoards.find(b => b.id === lastViewedId) : null;

            const targetBoard = lastViewedBoard
              || existingBoards.sort((a, b) =>
                  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                )[0];

            console.log('KanbanBoard: Loading board:', targetBoard.id, lastViewedBoard ? '(last viewed)' : '(most recent)');
            kanbanBoard = await kanbanService.getBoard(targetBoard.id);
          }
        }

        console.log('KanbanBoard: Loaded board:', kanbanBoard ? `Board with ${kanbanBoard.cards?.length || 0} cards` : 'No board found');

        // If no board exists, we need user info to create one - only then do we wait
        if (!kanbanBoard && !isReadOnly) {
          const defaultBoardId = platformContext.getBoardId();
          console.log('KanbanBoard: Creating default board with ID:', defaultBoardId);

          // Get user info for creating board
          let boardCreator: KanbanUser = {
            id: 'default-user',
            displayName: 'Default User',
            email: 'default@example.com',
          };

          try {
            const userInfo = await platformContext.getUserInfo();
            if (userInfo) {
              boardCreator = {
                id: userInfo.objectId || 'unknown',
                displayName: userInfo.displayName || 'Unknown User',
                email: userInfo.preferredUserName || '',
              };
              setCurrentUser(boardCreator);
            }
          } catch (error) {
            console.error('KanbanBoard: Error getting current user for board creation:', error);
          }

          kanbanBoard = await kanbanService.createBoard({
            boardId: defaultBoardId,
            title: 'Default',
            teamId: context?.teamId,
            channelId: context?.channelId,
            chatId: context?.chatId,
            members: [],
            createdBy: boardCreator,
          });

          const updatedBoards = await kanbanService.listBoards(prefix);
          setBoardList(updatedBoards);
        } else if (!kanbanBoard && isReadOnly) {
          setError('Board not found. The shared link may be invalid.');
          setLoading(false);
          return;
        }

        setBoard(kanbanBoard);

        // Remember last viewed board for this context
        if (kanbanBoard && prefix) {
          localStorage.setItem(`kanban_lastBoard_${prefix}`, kanbanBoard.id);
        }

        // Merge board members (custom users) into availableMembers immediately
        if (kanbanBoard && kanbanBoard.members && kanbanBoard.members.length > 0) {
          setAvailableMembers(kanbanBoard.members);
        }

        // Set the teams context for notifications
        if (kanbanBoard) {
          notificationService.setTeamsContext(
            kanbanBoard.teamId || null,
            kanbanBoard.channelId || null,
            context?.tenantId || null,
            kanbanBoard.id || null
          );
        }

        // Handle deep link
        if (context?.subEntityId && kanbanBoard) {
          console.log('Deep link detected - opening card:', context.subEntityId);
          // subEntityId can be a plain card ID or a JSON object with cardId
          let targetCardId = context.subEntityId;
          try {
            const parsed = JSON.parse(context.subEntityId);
            if (parsed.cardId) targetCardId = parsed.cardId;
          } catch (e) {
            // Not JSON, use as-is (plain card ID)
          }
          const cardToOpen = kanbanBoard.cards.find(c => c.id === targetCardId);
          if (cardToOpen) {
            console.log('Found card from deep link:', cardToOpen.title);
            setDeepLinkCardId(targetCardId);
          } else {
            console.warn('Card not found for subEntityId:', targetCardId);
          }
        }

        // Load cached photos immediately for assigned users (instant display)
        if (kanbanBoard && kanbanBoard.cards) {
          const cachedPhotos: Record<string, string> = {};
          const userIds = new Set<string>();
          kanbanBoard.cards.forEach((card: KanbanCard) => {
            if (card.assignedTo) {
              card.assignedTo.forEach((assignee: KanbanUser) => {
                if (assignee.id && !assignee.isCustomUser) {
                  userIds.add(assignee.id);
                }
              });
            }
          });
          userIds.forEach(userId => {
            const cached = getCachedPhoto(userId);
            if (cached && cached.photoData) {
              cachedPhotos[userId] = cached.photoData;
            }
          });
          if (Object.keys(cachedPhotos).length > 0) {
            setUserPhotos(cachedPhotos);
          }
        }

        // Board is ready - show it now!
        setLoading(false);

        // Now load team members and user info in the background
        loadMembersInBackground(kanbanBoard);

      } catch (error) {
        console.error('Failed to initialize board:', error);
        setError(`Failed to initialize board: ${(error as Error).message}`);
        setLoading(false);
      }
    };

    // Background member loading - doesn't block the UI
    const loadMembersInBackground = async (kanbanBoard: KanbanBoardType | null) => {
      try {
        // Get current user
        let user: KanbanUser | null = null;
        try {
          const userInfo = await platformContext.getUserInfo();
          if (userInfo) {
            user = {
              id: userInfo.objectId || 'unknown',
              displayName: userInfo.displayName || 'Unknown User',
              email: userInfo.preferredUserName || '',
            };
            setCurrentUser(user);
            console.log('KanbanBoard: Current user set:', user);
            if (platformContext.supportsNotifications()) {
              notificationService.setInitialized(true);
            }

            // Check license after we have user info
            const context = platformContext.getContext();
            const taskCount = kanbanBoard?.cards?.length || 0;
            const licenseResult = await checkLicense(userInfo.preferredUserName, context?.tenantId, taskCount);
            setLicense(licenseResult);
            console.log('KanbanBoard: License check result:', licenseResult);
          }
        } catch (error) {
          console.error('KanbanBoard: Error getting current user:', error);
          // On error, default to free tier
          setLicense(getFreeTierDefaults());
        }

        // Get team/chat members
        let members: KanbanUser[] = [];
        console.log('KanbanBoard: Getting team members in background...');

        try {
          const ssoToken = await platformContext.getAuthToken();

          if (ssoToken) {
            setAccessToken(ssoToken);
            const membersResult = await platformContext.getAvailableMembers();
            members = membersResult.members;
            console.log('KanbanBoard: Team members fetched:', members.length, 'members');

            // Show warning only if there was an actual error fetching members
            if (membersResult.error) {
              setMemberPermissionWarning(true);
            }

            // Load user photos asynchronously
            console.log(`KanbanBoard: Starting to load photos for ${members.length} members`);
            const photoPromises = members.map(async (member) => {
              const photoUrl = await loadUserPhoto(member.id, ssoToken);
              return { userId: member.id, photoUrl };
            });

            Promise.all(photoPromises).then((photos) => {
              let photoCount = 0;
              setUserPhotos(prevPhotos => {
                const newPhotos = { ...prevPhotos };
                photos.forEach(({ userId, photoUrl }) => {
                  if (photoUrl) {
                    newPhotos[userId] = photoUrl;
                    photoCount++;
                  }
                });
                console.log(`KanbanBoard: Successfully loaded ${photoCount} photos out of ${photos.length} users`);
                return newPhotos;
              });
            });

            // Also load photos for assigned users not in members list
            if (kanbanBoard && kanbanBoard.cards) {
              const assignedUserIds = new Set<string>();
              kanbanBoard.cards.forEach((card: KanbanCard) => {
                if (card.assignedTo) {
                  card.assignedTo.forEach((assignee: KanbanUser) => {
                    if (assignee.id && !assignee.isCustomUser) {
                      assignedUserIds.add(assignee.id);
                    }
                  });
                }
              });

              const memberIds = new Set(members.map(m => m.id));
              const additionalUserIds = Array.from(assignedUserIds).filter(id => !memberIds.has(id));

              if (additionalUserIds.length > 0) {
                console.log(`KanbanBoard: Loading photos for ${additionalUserIds.length} additional assigned users`);
                const additionalPhotoPromises = additionalUserIds.map(async (userId) => {
                  const photoUrl = await loadUserPhoto(userId, ssoToken);
                  return { userId, photoUrl };
                });

                Promise.all(additionalPhotoPromises).then((photos) => {
                  setUserPhotos(prevPhotos => {
                    const newPhotos = { ...prevPhotos };
                    photos.forEach(({ userId, photoUrl }) => {
                      if (photoUrl) {
                        newPhotos[userId] = photoUrl;
                      }
                    });
                    console.log(`KanbanBoard: Added photos for ${photos.filter(p => p.photoUrl).length} assigned users`);
                    return newPhotos;
                  });
                });
              }
            }
          }

          // Fallback if no members found
          if (members.length === 0 && user) {
            members = [user];
            console.log('KanbanBoard: No team members found, using current user');
          } else if (members.length === 0) {
            const currentUserInfo = await platformContext.getUserInfo();
            if (currentUserInfo && (currentUserInfo.displayName || currentUserInfo.preferredUserName)) {
              members = [{
                id: currentUserInfo.objectId || 'unknown-id',
                displayName: currentUserInfo.displayName || 'Unknown User',
                email: currentUserInfo.preferredUserName || 'unknown@example.com',
              }];
              console.log('KanbanBoard: Current user added as member:', members[0]);
            }
          }
        } catch (memberError) {
          console.warn('KanbanBoard: Could not get team members:', memberError);
          if (members.length === 0) {
            members = [{
              id: 'fallback-user',
              displayName: 'Anonymous User',
              email: 'anonymous@example.com',
            }];
            console.log('KanbanBoard: Using fallback user');
          }
        }

        // Merge with existing board members (custom users)
        if (kanbanBoard && kanbanBoard.members && kanbanBoard.members.length > 0) {
          const existingIds = new Set(members.map(m => m.id));
          const additionalMembers = kanbanBoard.members.filter((m: KanbanUser) => !existingIds.has(m.id));
          if (additionalMembers.length > 0) {
            members = [...members, ...additionalMembers];
            console.log('KanbanBoard: Merged board members:', additionalMembers.length, 'additional');
          }
        }

        // Ensure current user is in the board's stored members (for comment/card author resolution)
        if (kanbanBoard && user && !isReadOnly) {
          const boardMemberIds = new Set(kanbanBoard.members?.map((m: KanbanUser) => m.id) || []);
          if (!boardMemberIds.has(user.id)) {
            const updatedMembers = [...(kanbanBoard.members || []), user];
            await kanbanService.updateBoard(kanbanBoard.id, { members: updatedMembers });
            kanbanBoard.members = updatedMembers;
            console.log('KanbanBoard: Added current user to board members:', user.id);
          }
        }

        setAvailableMembers(members);
      } catch (error) {
        console.error('KanbanBoard: Error loading members in background:', error);
      }
    };

    initializeBoard();
  }, [boardId]);

  // Poll for board updates every 30 seconds
  useEffect(() => {
    if (!board || loading) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        console.log('Polling for board updates...');
        const syncedBoard = await kanbanService.syncBoard(board.id);

        if (syncedBoard && syncedBoard.updatedAt > board.updatedAt) {
          console.log('Board was updated, refreshing...');
          setBoard(syncedBoard);
        }
      } catch (error) {
        console.error('Failed to sync board:', error);
        // Don't show error to user for background sync failures
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [board, loading]);

  // Clear license cache and refresh when user returns to the app
  useEffect(() => {
    const refreshLicense = () => {
      clearLicenseCache();
      loadLicenseInfo();
    };

    // Visibility change (works in regular browsers)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshLicense();
      }
    };

    // Focus event (works better in Teams iframe)
    const handleFocus = () => {
      refreshLicense();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [currentUser]);

  // Scroll to newly added comment and highlight it
  useEffect(() => {
    console.log('useEffect triggered, newlyAddedCommentId:', newlyAddedCommentId);
    console.log('commentRefs.current:', Object.keys(commentRefs.current));
    console.log('commentsContainerRef.current:', commentsContainerRef.current);

    if (newlyAddedCommentId) {
      // Give React time to render and populate the ref
      const scrollTimer = setTimeout(() => {
        const commentElement = commentRefs.current[newlyAddedCommentId];
        const container = commentsContainerRef.current;

        console.log('Found comment element:', commentElement);
        console.log('Found container:', container);

        // Scroll the container to show the new comment
        if (commentElement && container) {
          console.log('Scrolling to comment...');
          // Get the position of the comment relative to the container
          const containerRect = container.getBoundingClientRect();
          const commentRect = commentElement.getBoundingClientRect();
          const scrollOffset = commentRect.top - containerRect.top;

          // Scroll the container smoothly
          container.scrollTo({
            top: container.scrollTop + scrollOffset - 20, // 20px padding from top
            behavior: 'smooth'
          });
        } else if (container) {
          // Fallback: just scroll to bottom
          console.log('Scrolling container to bottom...');
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);

      // Remove the highlight after animation completes (2s)
      const highlightTimer = setTimeout(() => {
        console.log('Removing highlight...');
        setNewlyAddedCommentId(null);
      }, 2000);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(highlightTimer);
      };
    }
  }, [newlyAddedCommentId]);

  const handleShareBoard = async () => {
    if (!board) return;

    try {
      // Get the current URL and add readonly parameter
      const baseUrl = window.location.origin + window.location.pathname;
      const shareUrl = `${baseUrl}?readonly=true&boardId=${encodeURIComponent(board.id)}`;

      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);

      // Show success feedback
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to copy share link:', error);
    }
  };

  // Board management handlers
  const loadBoardList = async () => {
    if (!contextPrefix) return;
    try {
      const boards = await kanbanService.listBoards(contextPrefix);
      setBoardList(boards);
    } catch (error) {
      console.error('Failed to load board list:', error);
    }
  };

  const handleSwitchBoard = async (newBoardId: string) => {
    if (!newBoardId || newBoardId === board?.id) return;
    try {
      setLoading(true);
      const newBoard = await kanbanService.getBoard(newBoardId);
      if (newBoard) {
        setBoard(newBoard);
        // Remember last viewed board
        if (contextPrefix) {
          localStorage.setItem(`kanban_lastBoard_${contextPrefix}`, newBoard.id);
        }
        // Update notification service with new board context
        const context = platformContext.getContext();
        notificationService.setTeamsContext(
          newBoard.teamId || null,
          newBoard.channelId || null,
          context?.tenantId || null,
          newBoard.id || null
        );
      }
    } catch (error) {
      console.error('Failed to switch board:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!newBoardTitle.trim() || !contextPrefix) return;
    try {
      const newBoardId = kanbanService.generateBoardId(contextPrefix);
      const context = platformContext.getContext();
      const boardCreator: KanbanUser = currentUser || availableMembers[0] || {
        id: 'default-user',
        displayName: 'Default User',
        email: 'default@example.com',
      };

      const newBoard = await kanbanService.createBoard({
        boardId: newBoardId,
        title: newBoardTitle.trim(),
        teamId: context?.teamId,
        channelId: context?.channelId,
        chatId: context?.chatId,
        members: availableMembers,
        createdBy: boardCreator,
      });

      setBoard(newBoard);
      // Remember last viewed board
      if (contextPrefix) {
        localStorage.setItem(`kanban_lastBoard_${contextPrefix}`, newBoard.id);
      }
      // Update notification service with new board context
      notificationService.setTeamsContext(
        newBoard.teamId || null,
        newBoard.channelId || null,
        context?.tenantId || null,
        newBoard.id || null
      );
      setNewBoardTitle('');
      setIsCreateBoardDialogOpen(false);
      await loadBoardList();
    } catch (error) {
      console.error('Failed to create board:', error);
    }
  };

  const handleRenameBoard = async () => {
    if (!editingBoardTitle.trim() || !board) return;
    try {
      const renamedBoard = await kanbanService.renameBoard(board.id, editingBoardTitle.trim());
      setBoard(renamedBoard);
      setEditingBoardTitle('');
      setIsRenameBoardDialogOpen(false);
      await loadBoardList();
    } catch (error) {
      console.error('Failed to rename board:', error);
    }
  };

  const handleDeleteBoard = async () => {
    if (!board || boardList.length <= 1) return;
    try {
      await kanbanService.deleteBoard(board.id);
      setIsDeleteBoardDialogOpen(false);

      // Switch to another board
      const remainingBoards = boardList.filter(b => b.id !== board.id);
      if (remainingBoards.length > 0) {
        await handleSwitchBoard(remainingBoards[0].id);
      }
      await loadBoardList();
    } catch (error) {
      console.error('Failed to delete board:', error);
    }
  };

  const handleOpenRenameDialog = () => {
    if (board) {
      setEditingBoardTitle(board.title);
      setIsRenameBoardDialogOpen(true);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !board || isReadOnly) return;

    const { source, destination, draggableId, type } = result;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    try {
      if (type === 'column') {
        // Handle column reordering - temporarily move and show confirmation dialog
        const sourceIndex = source.index;
        const destinationIndex = destination.index;
        const columnTitle = board.columns[sourceIndex].title;
        
        // Temporarily update the visual order
        const tempBoard = { ...board };
        const [removed] = tempBoard.columns.splice(sourceIndex, 1);
        tempBoard.columns.splice(destinationIndex, 0, removed);
        setBoard(tempBoard);
        
        setPendingColumnMove({
          sourceIndex,
          destinationIndex,
          columnTitle
        });
        setIsColumnMoveConfirmOpen(true);
      } else {
        // Handle card moving between cells (column-row combinations)
        const parseDroppableId = (id: string) => {
          const match = id.match(/^column-(.+)-row-(.+)$/);
          if (match) {
            return { columnId: match[1], rowId: match[2] };
          }
          return null;
        };

        const sourceLocation = parseDroppableId(source.droppableId);
        const destLocation = parseDroppableId(destination.droppableId);

        if (!sourceLocation || !destLocation) {
          console.error('Invalid droppable ID format');
          return;
        }

        const moveRequest: MoveCardRequest = {
          cardId: draggableId,
          targetColumnId: destLocation.columnId,
          targetRowId: destLocation.rowId,
          targetIndex: destination.index,
          movedBy: currentUser?.email,
        };

        // Optimistically update the board state so the card doesn't snap back
        const optimisticCards = board.cards.map(c => ({ ...c } as any));
        const cardIndex = optimisticCards.findIndex(c => c.id === draggableId);
        if (cardIndex !== -1) {
          const card = optimisticCards[cardIndex];
          card.columnId = destLocation.columnId;
          card.rowId = destLocation.rowId;

          // Remove the moved card from its current position
          optimisticCards.splice(cardIndex, 1);

          // Get the ordered list of cards in the destination cell (without the moved card)
          const destCellCards = optimisticCards
            .filter(c => c.columnId === destLocation.columnId && c.rowId === destLocation.rowId && !c.archived)
            .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

          // Insert the moved card at the target index
          destCellCards.splice(destination.index, 0, card);

          // Reassign sequential order values
          destCellCards.forEach((c, i) => { c.order = i; });

          // Put the moved card back into the full cards array
          optimisticCards.push(card);
          setBoard({ ...board, cards: optimisticCards });
        }

        // Persist to API in the background
        const updatedBoard = await kanbanService.moveCard(board.id, moveRequest);
        setBoard(updatedBoard);

        // Send notifications to assigned users about card move
        if (currentUser) {
          const movedCard = updatedBoard.cards.find(c => c.id === draggableId);
          if (movedCard && movedCard.assignedTo) {
            notificationService.notifyMove(
              movedCard.assignedTo,
              currentUser.id,
              movedCard.title,
              currentUser.displayName,
              movedCard.id
            );
          }
        }
      }
    } catch (error) {
      console.error('Failed to move item:', error);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!newCard.title?.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!selectedColumn) {
      errors.column = 'Please select a column';
    }

    if (!selectedRowForCard) {
      errors.row = 'Please select a row';
    }

    // Date validation
    if (newCard.startDate && newCard.endDate) {
      const startDate = new Date(newCard.startDate);
      const endDate = new Date(newCard.endDate);
      
      if (endDate <= startDate) {
        errors.endDate = 'End date must be after start date';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCard = async () => {
    if (!board || isReadOnly) return;

    // Check license task limit
    if (license && !canAddTask(license, board.cards?.length || 0)) {
      const message = license.isExpired
        ? `License expired. Task limit of ${license.restrictions.maxTasksPerBoard} reached. Renew to add more tasks.`
        : `Task limit reached. Free plan allows ${license.restrictions.maxTasksPerBoard} tasks per board. Get a license to add more.`;
      setValidationErrors({ title: message });
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const column = board.columns.find(col => col.id === selectedColumn);
      if (!column) {
        console.error('Selected column not found');
        return;
      }

      // Use the selected row from the new state
      const selectedRow = selectedRowForCard || (board.rows.length > 0 ? board.rows[0].id : '');
      if (!selectedRow) {
        console.error('No row available for card creation');
        return;
      }
      
      const createRequest: CreateCardRequest = {
        title: newCard.title!,
        description: newCard.description,
        columnId: selectedColumn,
        rowId: selectedRow,
        priority: newCard.priority || 'medium',
        assignedTo: selectedAssignees,
        color: newCard.color,
        startDate: newCard.startDate,
        endDate: newCard.endDate,
        createdBy: currentUser?.email,
        lastModifiedBy: currentUser?.email,
      };

      const updatedBoard = await kanbanService.createCard(board.id, createRequest);
      setBoard(updatedBoard);
      
      // Reset form
      setNewCard({ title: '', description: '', priority: 'medium', assignedTo: [], color: '#2196F3', startDate: '', endDate: '' });
      setSelectedAssignees([]);
      setSelectedColumn('');
      setSelectedRowForCard('');
      setValidationErrors({});
      setIsCreateCardDialogOpen(false);
    } catch (error) {
      console.error('Failed to create card:', error);
    }
  };

  const autoSaveCard = async (overrides?: {
    assignees?: string[];
    priority?: 'low' | 'medium' | 'high';
    color?: string;
  }) => {
    if (!board || !editingCard || isReadOnly) return;

    // Validate required fields
    if (!newCard.title?.trim()) {
      return; // Don't save if title is empty
    }

    // Date validation
    if (newCard.startDate && newCard.endDate) {
      const startDate = new Date(newCard.startDate);
      const endDate = new Date(newCard.endDate);

      if (endDate <= startDate) {
        setValidationErrors({ ...validationErrors, endDate: 'End date must be after start date' });
        return;
      }
    }

    try {
      // Use overrides if provided, otherwise use current state values
      const assigneesToSave = overrides?.assignees !== undefined ? overrides.assignees : selectedAssignees;
      const priorityToSave = overrides?.priority !== undefined ? overrides.priority : (newCard.priority || 'medium');
      const colorToSave = overrides?.color !== undefined ? overrides.color : newCard.color;

      const updateRequest = {
        id: editingCard.id,
        title: newCard.title?.trim() || '',
        description: newCard.description || '',
        priority: priorityToSave,
        assignedTo: assigneesToSave,
        color: colorToSave,
        startDate: newCard.startDate,
        endDate: newCard.endDate,
        updatedBy: currentUser?.email,
      };

      const updatedBoard = await kanbanService.updateCard(board.id, updateRequest);
      setBoard(updatedBoard);

      // Update the editing card to reflect changes
      const updatedCard = updatedBoard.cards.find(c => c.id === editingCard.id);
      if (updatedCard) {
        setEditingCard(updatedCard);

        // Only mark notification pending if actual changes were made
        if (currentUser && updatedCard.assignedTo && updatedCard.assignedTo.length > 0 && originalCardSnapshot) {
          const currentSnapshot = {
            title: updateRequest.title,
            description: updateRequest.description,
            priority: updateRequest.priority,
            assignedTo: assigneesToSave.map((u: any) => typeof u === 'string' ? u : u.id).sort(),
            color: updateRequest.color || '',
            startDate: updateRequest.startDate || '',
            endDate: updateRequest.endDate || '',
          };
          const originalAssignees = [...originalCardSnapshot.assignedTo].sort();
          const hasChanges =
            currentSnapshot.title !== originalCardSnapshot.title ||
            currentSnapshot.description !== originalCardSnapshot.description ||
            currentSnapshot.priority !== originalCardSnapshot.priority ||
            currentSnapshot.color !== originalCardSnapshot.color ||
            currentSnapshot.startDate !== originalCardSnapshot.startDate ||
            currentSnapshot.endDate !== originalCardSnapshot.endDate ||
            JSON.stringify(currentSnapshot.assignedTo) !== JSON.stringify(originalAssignees);

          if (hasChanges) {
            console.log('[NOTIFICATION DEBUG] Marking pending notification for update - actual changes detected');
            const assigneesChanged = JSON.stringify(currentSnapshot.assignedTo) !== JSON.stringify(originalAssignees);
            setPendingNotifications(prev => {
              const next = new Set(prev);
              next.add(assigneesChanged ? 'assignment' : 'update');
              return next;
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to auto-save card:', error);
    }
  };

  // Helper function to close the edit dialog and send any pending notifications
  const closeEditDialog = () => {
    // Send pending notifications — use the most specific action type
    if (pendingNotifications.size > 0 && editingCard && currentUser && editingCard.assignedTo && editingCard.assignedTo.length > 0) {
      console.log('[NOTIFICATION DEBUG] Sending deferred notifications on dialog close:', Array.from(pendingNotifications));
      const assignees = editingCard.assignedTo;
      const userId = currentUser.id;
      const title = editingCard.title;
      const actor = currentUser.displayName;
      const cardId = editingCard.id;

      if (pendingNotifications.has('assignment')) {
        notificationService.notifyAssignment(assignees, userId, title, actor, cardId);
      }
      if (pendingNotifications.has('comment')) {
        notificationService.notifyComment(assignees, userId, title, actor, cardId);
      }
      if (pendingNotifications.has('attachment')) {
        notificationService.notifyAttachment(assignees, userId, title, actor, cardId);
      }
      if (pendingNotifications.has('update') && !pendingNotifications.has('assignment')) {
        notificationService.notifyUpdate(assignees, userId, title, actor, cardId);
      }
    }

    // Reset dialog state
    setIsEditCardDialogOpen(false);
    setNewCard({ title: '', description: '', priority: 'medium', assignedTo: [], startDate: '', endDate: '' });
    setSelectedAssignees([]);
    setValidationErrors({});
    setEditingCard(null);
    setAttachmentError(null);
    setNewChecklistItem('');
    setPendingNotifications(new Set());
    setOriginalCardSnapshot(null);
  };

  const removeAssignee = async (userId: string) => {
    const newAssignees = selectedAssignees.filter(id => id !== userId);
    setSelectedAssignees(newAssignees);

    // Save the card first with the updated assignees
    await autoSaveCard({ assignees: newAssignees });

    // Then check if the removed user is a custom user and should be cleaned up
    const removedUser = availableMembers.find(m => m.id === userId);
    if (removedUser?.isCustomUser && board) {
      // Refetch the board to get the latest card data
      const freshBoard = await kanbanService.getBoard(board.id);
      if (!freshBoard) return;

      // Check if this custom user is assigned to any other cards
      const isUsedElsewhere = freshBoard.cards.some(card => {
        return card.assignedTo?.some(user => user.id === userId);
      });

      if (!isUsedElsewhere) {
        // Remove from availableMembers
        const updatedMembers = availableMembers.filter(m => m.id !== userId);
        setAvailableMembers(updatedMembers);

        // Remove from board.members and save to database
        const updatedBoardMembers = freshBoard.members.filter(m => m.id !== userId);
        await kanbanService.updateBoard(freshBoard.id, { members: updatedBoardMembers });

        // Refetch to get the clean state
        const cleanBoard = await kanbanService.getBoard(freshBoard.id);
        if (cleanBoard) {
          setBoard(cleanBoard);
        }
        console.log('Removed unused custom user from board:', removedUser.displayName);
      }
    }
  };

  // Handle assignee selection changes from Combobox (may add or remove multiple users)
  const handleAssigneeSelectionChange = async (newSelectedOptions: string[]) => {
    // Find which users were added and removed
    const addedUserIds = newSelectedOptions.filter(id => !selectedAssignees.includes(id));
    const removedUserIds = selectedAssignees.filter(id => !newSelectedOptions.includes(id));

    // Update state and save the card first
    setSelectedAssignees(newSelectedOptions);

    // Before saving the card, ensure newly assigned users are in board.members
    if (addedUserIds.length > 0 && board) {
      const currentMemberIds = new Set(board.members.map(m => m.id));
      const newMembers = addedUserIds
        .filter(id => !currentMemberIds.has(id))
        .map(id => availableMembers.find(m => m.id === id))
        .filter((m): m is KanbanUser => m !== undefined);

      if (newMembers.length > 0) {
        const updatedMembers = [...board.members, ...newMembers];
        await kanbanService.updateBoard(board.id, { members: updatedMembers });
        // Update local board state with new members
        setBoard({ ...board, members: updatedMembers });
      }
    }

    await autoSaveCard({ assignees: newSelectedOptions });

    // Then clean up any removed custom users that are no longer used
    const customUsersRemoved = removedUserIds.filter(userId => {
      const user = availableMembers.find(m => m.id === userId);
      return user?.isCustomUser;
    });

    if (customUsersRemoved.length > 0 && board) {
      // Refetch the board to get the latest card data
      const freshBoard = await kanbanService.getBoard(board.id);
      if (!freshBoard) return;

      for (const userId of customUsersRemoved) {
        const removedUser = availableMembers.find(m => m.id === userId);
        // Check if this custom user is assigned to any other cards
        const isUsedElsewhere = freshBoard.cards.some(card => {
          return card.assignedTo?.some(user => user.id === userId);
        });

        if (!isUsedElsewhere) {
          // Remove from availableMembers
          const updatedMembers = availableMembers.filter(m => m.id !== userId);
          setAvailableMembers(updatedMembers);

          // Remove from board.members and save to database
          const updatedBoardMembers = freshBoard.members.filter(m => m.id !== userId);
          await kanbanService.updateBoard(freshBoard.id, { members: updatedBoardMembers });

          // Refetch to get the clean state
          const cleanBoard = await kanbanService.getBoard(freshBoard.id);
          if (cleanBoard) {
            setBoard(cleanBoard);
          }
          console.log('Removed unused custom user from board:', removedUser?.displayName);
        }
      }
    }
  };

  const addCustomUser = async () => {
    const trimmedName = customUserInput.trim();
    if (!trimmedName) return;

    // Check if user already exists
    const existingMember = availableMembers.find(m => m.displayName.toLowerCase() === trimmedName.toLowerCase());
    if (existingMember) {
      // User exists, just add them to selected assignees
      if (!selectedAssignees.includes(existingMember.id)) {
        const newAssignees = [...selectedAssignees, existingMember.id];
        setSelectedAssignees(newAssignees);
        setTimeout(() => autoSaveCard({ assignees: newAssignees }), 0);
      }
      setCustomUserInput('');
      return;
    }

    // Generate a unique ID for the custom user
    const customUserId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newCustomUser: KanbanUser = {
      id: customUserId,
      displayName: trimmedName,
      isCustomUser: true,
    };

    // Add to available members
    const updatedMembers = [...availableMembers, newCustomUser];
    setAvailableMembers(updatedMembers);

    // Add to board members and save to database
    if (board) {
      const updatedBoard = {
        ...board,
        members: [...board.members, newCustomUser],
      };
      setBoard(updatedBoard);
      await kanbanService.updateBoard(board.id, { members: updatedBoard.members });
    }

    // Add to selected assignees
    const newAssignees = [...selectedAssignees, customUserId];
    setSelectedAssignees(newAssignees);
    setTimeout(() => autoSaveCard({ assignees: newAssignees }), 0);

    // Clear input
    setCustomUserInput('');
  };

  // Clean up custom users that are no longer assigned to any cards
  const cleanupUnusedCustomUsers = async (boardData: KanbanBoardType): Promise<KanbanBoardType> => {
    // Get all user IDs that are currently assigned to cards
    const usedUserIds = new Set<string>();
    boardData.cards.forEach(card => {
      card.assignedTo?.forEach(user => usedUserIds.add(user.id));
    });

    // Find custom users that are no longer used
    const unusedCustomUsers = boardData.members.filter(
      member => member.isCustomUser && !usedUserIds.has(member.id)
    );

    if (unusedCustomUsers.length === 0) {
      return boardData;
    }

    // Remove unused custom users from board members
    const unusedIds = new Set(unusedCustomUsers.map(u => u.id));
    const updatedMembers = boardData.members.filter(m => !unusedIds.has(m.id));

    // Update the board in the database
    await kanbanService.updateBoard(boardData.id, { members: updatedMembers });

    // Update local available members state
    setAvailableMembers(prev => prev.filter(m => !unusedIds.has(m.id)));

    console.log('Cleaned up unused custom users:', unusedCustomUsers.map(u => u.displayName));

    return { ...boardData, members: updatedMembers };
  };

  const openEditDialog = (card: any) => {
    setEditingCard(card);

    // Output notification detail URL for testing
    if (board) {
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const testUrl = `${baseUrl}/index.html#/notification-detail?cardId=${encodeURIComponent(card.id)}&boardId=${encodeURIComponent(board.id)}&teamId=${board.teamId || ''}&channelId=${board.channelId || ''}`;
      console.log('🔗 Notification Detail URL (for testing):', testUrl);
    }

    // Helper function to convert date to local date string for input
    const dateToInputValue = (date: Date | string | undefined) => {
      if (!date) return '';
      const d = new Date(date);
      // Adjust for timezone offset to prevent day shifting
      const offset = d.getTimezoneOffset();
      const localDate = new Date(d.getTime() - (offset * 60 * 1000));
      return localDate.toISOString().split('T')[0];
    };
    
    const cardSnapshot = {
      title: card.title,
      description: card.description || '',
      priority: card.priority,
      assignedTo: card.assignedTo?.map((user: any) => user.id) || [],
      color: card.color || '#2196F3',
      startDate: dateToInputValue(card.startDate),
      endDate: dateToInputValue(card.endDate),
    };
    setNewCard(cardSnapshot);
    setSelectedAssignees(card.assignedTo?.map((user: any) => user.id) || []);
    setOriginalCardSnapshot(cardSnapshot);
    setValidationErrors({});
    setAttachmentError(null);
    setNewChecklistItem('');
    setIsEditCardDialogOpen(true);
  };

  // Handle deep link card opening after board and openEditDialog are ready
  useEffect(() => {
    if (deepLinkCardId && board) {
      const cardToOpen = board.cards.find(c => c.id === deepLinkCardId);
      if (cardToOpen) {
        console.log('Opening card from deep link:', cardToOpen.title);
        openEditDialog(cardToOpen);
        setDeepLinkCardId(null);
      }
    }
  }, [deepLinkCardId, board]);

  const openDeleteDialog = (card: any) => {
    if (isReadOnly) return;
    setCardToDelete(card);
    setIsDeleteCardDialogOpen(true);
  };

  const handleDeleteCard = async () => {
    if (!board || !cardToDelete || isReadOnly) return;

    try {
      const updatedBoard = await kanbanService.deleteCard(board.id, cardToDelete.id);
      // Clean up any custom users that were only assigned to the deleted card
      const cleanedBoard = await cleanupUnusedCustomUsers(updatedBoard);
      setBoard(cleanedBoard);
      setIsDeleteCardDialogOpen(false);
      setCardToDelete(null);
    } catch (error) {
      console.error('Error deleting card:', error);
    }
  };

  const handleArchiveCard = async (card: any) => {
    if (!board || isReadOnly) return;

    try {
      const updatedBoard = await kanbanService.archiveCard(board.id, card.id, currentUser?.email);
      setBoard(updatedBoard);
    } catch (error) {
      console.error('Error archiving card:', error);
    }
  };

  const handleUnarchiveCard = async (cardId: string) => {
    if (!board || isReadOnly) return;

    try {
      const updatedBoard = await kanbanService.unarchiveCard(board.id, cardId, currentUser?.email);
      setBoard(updatedBoard);
      // Refresh archived cards list
      const archived = await kanbanService.getArchivedCards(board.id);
      setArchivedCards(archived);
    } catch (error) {
      console.error('Error unarchiving card:', error);
    }
  };

  const handleOpenArchiveDialog = async () => {
    if (!board) return;

    try {
      const archived = await kanbanService.getArchivedCards(board.id);
      setArchivedCards(archived);
      setArchiveSearchQuery('');
      setIsArchiveDialogOpen(true);
    } catch (error) {
      console.error('Error loading archived cards:', error);
    }
  };

  const getFilteredArchivedCards = () => {
    if (!archiveSearchQuery.trim()) {
      return archivedCards;
    }

    const lowercaseQuery = archiveSearchQuery.toLowerCase();
    return archivedCards.filter(card =>
      card.title.toLowerCase().includes(lowercaseQuery) ||
      card.description?.toLowerCase().includes(lowercaseQuery) ||
      card.tags?.some((tag: string) => tag.toLowerCase().includes(lowercaseQuery)) ||
      card.assignedTo?.some((user: any) => user.displayName.toLowerCase().includes(lowercaseQuery))
    );
  };

  const handleConfirmArchive = async () => {
    if (!archiveConfirmData) return;

    if (archiveConfirmData.type === 'column') {
      await performArchiveColumnCards(archiveConfirmData.id);
    } else {
      await performArchiveRowCards(archiveConfirmData.id);
    }

    setIsArchiveConfirmOpen(false);
    setArchiveConfirmData(null);
  };

  const formatCommentDate = (date: Date) => {
    const now = new Date();
    const commentDate = new Date(date);
    const diffMs = now.getTime() - commentDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // For older comments, show date
    return commentDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: commentDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const handleAddComment = async () => {
    if (!board || !editingCard || !newComment.trim() || isReadOnly || !currentUser) return;

    try {
      const updatedBoard = await kanbanService.addComment(board.id, editingCard.id, newComment, currentUser);
      setBoard(updatedBoard);

      // Update editingCard with new comments
      const updatedCard = updatedBoard.cards.find(c => c.id === editingCard.id);
      if (updatedCard) {
        setEditingCard(updatedCard);

        // Get the ID of the newly added comment (last comment in the array)
        if (updatedCard.comments && updatedCard.comments.length > 0) {
          const newCommentId = updatedCard.comments[updatedCard.comments.length - 1].id;
          console.log('Setting newly added comment ID:', newCommentId);
          setNewlyAddedCommentId(newCommentId);
        }

        // Mark that a notification should be sent when the dialog closes
        if (currentUser && updatedCard.assignedTo && updatedCard.assignedTo.length > 0) {
          console.log('[NOTIFICATION DEBUG] Marking pending notification for comment');
          setPendingNotifications(prev => {
            const next = new Set(prev);
            next.add('comment');
            return next;
          });
        }
      }

      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!board || !editingCard || isReadOnly) return;

    try {
      const updatedBoard = await kanbanService.deleteComment(board.id, editingCard.id, commentId);
      setBoard(updatedBoard);

      // Update editingCard with updated comments
      const updatedCard = updatedBoard.cards.find(c => c.id === editingCard.id);
      if (updatedCard) {
        setEditingCard(updatedCard);
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!board || !editingCard || isReadOnly) return;

    try {
      const updatedBoard = await kanbanService.removeAttachment(board.id, editingCard.id, attachmentId);
      setBoard(updatedBoard);
      const updatedCard = updatedBoard.cards.find((c: any) => c.id === editingCard.id);
      setEditingCard(updatedCard);
    } catch (error) {
      console.error('Error removing attachment:', error);
    }
  };

  const handleEditComment = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditingCommentText(currentText);
  };

  const handleSaveEditComment = async () => {
    if (!board || !editingCard || !editingCommentId || !editingCommentText.trim() || isReadOnly) return;

    try {
      const updatedBoard = await kanbanService.updateComment(board.id, editingCard.id, editingCommentId, editingCommentText);
      setBoard(updatedBoard);

      // Update editingCard with updated comments
      const updatedCard = updatedBoard.cards.find(c => c.id === editingCard.id);
      if (updatedCard) {
        setEditingCard(updatedCard);
      }

      setEditingCommentId(null);
      setEditingCommentText('');
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  // Checklist handlers for EditTaskDialog
  const handleAddChecklistItem = async () => {
    if (!board || !editingCard || !newChecklistItem.trim()) return;
    try {
      const updatedBoard = await kanbanService.addChecklistItem(board.id, editingCard.id, newChecklistItem.trim());
      setBoard(updatedBoard);
      const updatedCard = updatedBoard.cards.find((c: any) => c.id === editingCard.id);
      setEditingCard(updatedCard || null);
      setNewChecklistItem('');
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  const handleToggleChecklistItem = async (itemId: string) => {
    if (!board || !editingCard || isReadOnly) return;
    try {
      const updatedBoard = await kanbanService.toggleChecklistItem(board.id, editingCard.id, itemId);
      setBoard(updatedBoard);
      const updatedCard = updatedBoard.cards.find((c: any) => c.id === editingCard.id);
      setEditingCard(updatedCard || null);
    } catch (error) {
      console.error('Error toggling checklist item:', error);
    }
  };

  const handleDeleteChecklistItem = async (itemId: string) => {
    if (!board || !editingCard) return;
    try {
      const updatedBoard = await kanbanService.deleteChecklistItem(board.id, editingCard.id, itemId);
      setBoard(updatedBoard);
      const updatedCard = updatedBoard.cards.find((c: any) => c.id === editingCard.id);
      setEditingCard(updatedCard || null);
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  // File upload handler for EditTaskDialog
  const handleFileUpload = async (file: File) => {
    if (!board || !editingCard || !currentUser) return;

    // Clear previous errors
    setAttachmentError(null);

    // Check file size (25MB = 25 * 1024 * 1024 bytes)
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      setAttachmentError(`File size exceeds 25MB limit. Selected file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      return;
    }

    try {
      const updatedBoard = await kanbanService.addAttachment(board.id, editingCard.id, file, currentUser);
      setBoard(updatedBoard);
      const updatedCard = updatedBoard.cards.find((c: any) => c.id === editingCard.id);
      setEditingCard(updatedCard || null);

      // Mark that a notification should be sent when the dialog closes
      if (currentUser && updatedCard && updatedCard.assignedTo && updatedCard.assignedTo.length > 0) {
        console.log('[NOTIFICATION DEBUG] Marking pending notification for attachment');
        setPendingNotifications(prev => {
          const next = new Set(prev);
          next.add('attachment');
          return next;
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setAttachmentError(error instanceof Error ? error.message : 'Failed to upload file. Please try again.');
    }
  };

  const startEditingColumn = (columnId: string, currentTitle: string) => {
    setEditingColumn(columnId);
    setEditingColumnTitle(currentTitle);
  };

  const handleColumnTitleSubmit = async () => {
    if (!board || !editingColumn || !editingColumnTitle.trim()) {
      setEditingColumn(null);
      setEditingColumnTitle('');
      return;
    }

    try {
      const updatedBoard = { ...board };
      const column = updatedBoard.columns.find(col => col.id === editingColumn);
      if (column) {
        column.title = editingColumnTitle.trim();
        updatedBoard.updatedAt = new Date();
        
        const savedBoard = await kanbanService.updateBoard(board.id, updatedBoard);
        setBoard(savedBoard);
      }
    } catch (error) {
      console.error('Failed to update column title:', error);
    } finally {
      setEditingColumn(null);
      setEditingColumnTitle('');
    }
  };

  const handleColumnTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleColumnTitleSubmit();
    } else if (e.key === 'Escape') {
      setEditingColumn(null);
      setEditingColumnTitle('');
    }
  };

  const handleAddColumn = async () => {
    if (!board || !newColumnTitle.trim()) {
      setIsAddColumnDialogOpen(false);
      setNewColumnTitle('');
      return;
    }

    try {
      const updatedBoard = { ...board };
      const maxOrder = Math.max(...updatedBoard.columns.map(col => col.order));
      
      const newColumn = {
        id: Math.random().toString(36).substring(2) + Date.now().toString(36),
        title: newColumnTitle.trim(),
        rows: [
          {
            id: Math.random().toString(36).substring(2) + Date.now().toString(36),
            name: 'Tasks',
            cards: [],
            order: 0,
          }
        ],
        order: maxOrder + 1,
        color: '#F3F4F6', // Default light gray color
      };

      updatedBoard.columns.push(newColumn);
      updatedBoard.updatedAt = new Date();
      
      const savedBoard = await kanbanService.updateBoard(board.id, updatedBoard);
      setBoard(savedBoard);
    } catch (error) {
      console.error('Failed to add column:', error);
    } finally {
      setIsAddColumnDialogOpen(false);
      setNewColumnTitle('');
    }
  };

  const startDeleteColumn = (columnId: string) => {
    const column = board?.columns.find(col => col.id === columnId);
    if (!column || !board) return;

    setColumnToDelete(columnId);
    setIsDeleteColumnDialogOpen(true);
  };

  const handleArchiveColumnCards = async (columnId: string) => {
    if (!board || isReadOnly) return;

    // Count active cards in this column
    const activeCardsCount = board.cards.filter(card => card.columnId === columnId && !card.archived).length;

    // If more than one card, show confirmation
    if (activeCardsCount > 1) {
      const column = board.columns.find(col => col.id === columnId);
      setArchiveConfirmData({
        type: 'column',
        id: columnId,
        count: activeCardsCount,
        name: column?.title || 'Unknown'
      });
      setIsArchiveConfirmOpen(true);
      return;
    }

    // Archive without confirmation if 0 or 1 card
    await performArchiveColumnCards(columnId);
  };

  const performArchiveColumnCards = async (columnId: string) => {
    if (!board || isReadOnly) return;

    try {
      // Get all active cards in this column
      const cardsToArchive = board.cards
        .filter(card => card.columnId === columnId && !card.archived)
        .map(card => card.id);

      if (cardsToArchive.length === 0) return;

      // Archive all cards using the new API method
      const savedBoard = await kanbanService.archiveCards(board.id, cardsToArchive, currentUser?.email);
      setBoard(savedBoard);
    } catch (error) {
      console.error('Failed to archive column cards:', error);
    }
  };

  const openDeleteColumnCardsConfirm = (columnId: string) => {
    if (!board || isReadOnly) return;
    const column = board.columns.find(col => col.id === columnId);
    const cardCount = board.cards.filter(card => card.columnId === columnId && !card.archived).length;
    if (cardCount === 0) return;
    setDeleteCardsConfirmData({
      type: 'column',
      id: columnId,
      count: cardCount,
      name: column?.title || 'Unknown',
    });
    setIsDeleteCardsConfirmOpen(true);
  };

  const handleDeleteColumnCards = async (columnId: string) => {
    if (!board || isReadOnly) return;

    try {
      // Get all active (non-archived) cards in this column
      const cardsToDelete = board.cards
        .filter(card => card.columnId === columnId && !card.archived)
        .map(card => card.id);

      if (cardsToDelete.length === 0) return;

      // Delete all cards using the new API method
      const savedBoard = await kanbanService.deleteCards(board.id, cardsToDelete);
      // Clean up any custom users that were only assigned to deleted cards
      const cleanedBoard = await cleanupUnusedCustomUsers(savedBoard);
      setBoard(cleanedBoard);
    } catch (error) {
      console.error('Failed to delete column cards:', error);
    }
  };

  const handleDeleteColumn = async () => {
    if (!board || !columnToDelete) return;

    try {
      // Find the first remaining column (after deletion)
      const remainingColumns = board.columns.filter(col => col.id !== columnToDelete);
      const firstColumn = remainingColumns.length > 0 ? remainingColumns[0] : null;

      // Separate cards into those to move and those to delete
      const cardsInColumn = board.cards.filter(card => card.columnId === columnToDelete);
      const archivedCardsToMove = cardsInColumn.filter(card => card.archived && firstColumn);
      const cardsToDelete = cardsInColumn.filter(card => !card.archived || !firstColumn);

      // Move archived cards to the first column
      for (const card of archivedCardsToMove) {
        await kanbanService.updateCard(board.id, {
          id: card.id,
          columnId: firstColumn!.id,
          updatedBy: currentUser?.email,
        });
      }

      // Delete active cards
      if (cardsToDelete.length > 0) {
        await kanbanService.deleteCards(board.id, cardsToDelete.map(c => c.id));
      }

      // Remove the column from the board
      const savedBoard = await kanbanService.updateBoard(board.id, {
        columns: remainingColumns,
      });
      setBoard(savedBoard);
    } catch (error) {
      console.error('Failed to delete column:', error);
    } finally {
      setIsDeleteColumnDialogOpen(false);
      setColumnToDelete(null);
    }
  };

  const handleCreateRow = async () => {
    if (!board || !newRowName.trim()) {
      setIsCreateRowDialogOpen(false);
      setNewRowName('');
      return;
    }

    try {
      const updatedBoard = await kanbanService.createRow(board.id, {
        name: newRowName.trim(),
      });
      setBoard(updatedBoard);
    } catch (error) {
      console.error('Failed to create row:', error);
    } finally {
      setIsCreateRowDialogOpen(false);
      setNewRowName('');
    }
  };

  const startEditingRow = (rowId: string, currentName: string) => {
    setEditingRow(rowId);
    setEditingRowName(currentName);
  };

  const handleRowNameSubmit = async () => {
    if (!board || !editingRow || !editingRowName.trim()) {
      setEditingRow(null);
      setEditingRowName('');
      return;
    }

    try {
      const updatedBoard = await kanbanService.updateRow(board.id, {
        rowId: editingRow,
        name: editingRowName.trim(),
      });
      setBoard(updatedBoard);
    } catch (error) {
      console.error('Failed to update row name:', error);
    } finally {
      setEditingRow(null);
      setEditingRowName('');
    }
  };

  const handleRowNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRowNameSubmit();
    } else if (e.key === 'Escape') {
      setEditingRow(null);
      setEditingRowName('');
    }
  };

  const startDeleteRow = (rowId: string) => {
    if (!board) return;

    setRowToDelete(rowId);
    setIsDeleteRowDialogOpen(true);
  };

  const handleArchiveRowCards = async (rowId: string) => {
    if (!board || isReadOnly) return;

    // Count active cards in this row
    const activeCardsCount = board.cards.filter(card => card.rowId === rowId && !card.archived).length;

    // If more than one card, show confirmation
    if (activeCardsCount > 1) {
      const row = board.rows.find(r => r.id === rowId);
      setArchiveConfirmData({
        type: 'row',
        id: rowId,
        count: activeCardsCount,
        name: row?.name || 'Unknown'
      });
      setIsArchiveConfirmOpen(true);
      return;
    }

    // Archive without confirmation if 0 or 1 card
    await performArchiveRowCards(rowId);
  };

  const performArchiveRowCards = async (rowId: string) => {
    if (!board || isReadOnly) return;

    try {
      // Get all active cards in this row
      const cardsToArchive = board.cards
        .filter(card => card.rowId === rowId && !card.archived)
        .map(card => card.id);

      if (cardsToArchive.length === 0) return;

      // Archive all cards using the new API method
      const savedBoard = await kanbanService.archiveCards(board.id, cardsToArchive, currentUser?.email);
      setBoard(savedBoard);
    } catch (error) {
      console.error('Failed to archive row cards:', error);
    }
  };

  const openDeleteRowCardsConfirm = (rowId: string) => {
    if (!board || isReadOnly) return;
    const row = board.rows.find(r => r.id === rowId);
    const cardCount = board.cards.filter(card => card.rowId === rowId && !card.archived).length;
    if (cardCount === 0) return;
    setDeleteCardsConfirmData({
      type: 'row',
      id: rowId,
      count: cardCount,
      name: row?.name || 'Unknown',
    });
    setIsDeleteCardsConfirmOpen(true);
  };

  const handleDeleteRowCards = async (rowId: string) => {
    if (!board || isReadOnly) return;

    try {
      // Get all active (non-archived) cards in this row
      const cardsToDelete = board.cards
        .filter(card => card.rowId === rowId && !card.archived)
        .map(card => card.id);

      if (cardsToDelete.length === 0) return;

      // Delete all cards using the new API method
      const savedBoard = await kanbanService.deleteCards(board.id, cardsToDelete);
      // Clean up any custom users that were only assigned to deleted cards
      const cleanedBoard = await cleanupUnusedCustomUsers(savedBoard);
      setBoard(cleanedBoard);
    } catch (error) {
      console.error('Failed to delete row cards:', error);
    }
  };

  const handleConfirmDeleteCards = async () => {
    if (!deleteCardsConfirmData) return;

    if (deleteCardsConfirmData.type === 'column') {
      await handleDeleteColumnCards(deleteCardsConfirmData.id);
    } else {
      await handleDeleteRowCards(deleteCardsConfirmData.id);
    }

    setIsDeleteCardsConfirmOpen(false);
    setDeleteCardsConfirmData(null);
  };

  const handleDeleteRow = async () => {
    if (!board || !rowToDelete) return;

    try {
      // Find the first remaining row (after deletion)
      const remainingRows = board.rows.filter(row => row.id !== rowToDelete);
      const firstRow = remainingRows.length > 0 ? remainingRows[0] : null;

      // Separate cards into those to move and those to delete
      const cardsInRow = board.cards.filter(card => card.rowId === rowToDelete);
      const archivedCardsToMove = cardsInRow.filter(card => card.archived && firstRow);
      const cardsToDelete = cardsInRow.filter(card => !card.archived || !firstRow);

      // Move archived cards to the first row
      for (const card of archivedCardsToMove) {
        await kanbanService.updateCard(board.id, {
          id: card.id,
          rowId: firstRow!.id,
          updatedBy: currentUser?.email,
        });
      }

      // Delete active cards
      if (cardsToDelete.length > 0) {
        await kanbanService.deleteCards(board.id, cardsToDelete.map(c => c.id));
      }

      // Remove the row from the board
      const savedBoard = await kanbanService.updateBoard(board.id, {
        rows: remainingRows,
      });
      setBoard(savedBoard);
    } catch (error) {
      console.error('Failed to delete row:', error);
    } finally {
      setIsDeleteRowDialogOpen(false);
      setRowToDelete(null);
    }
  };

  const toggleRowCollapsed = (rowId: string) => {
    setCollapsedRows(prev => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(rowId)) {
        newCollapsed.delete(rowId);
      } else {
        newCollapsed.add(rowId);
      }
      return newCollapsed;
    });
  };

  const handleConfirmColumnMove = async () => {
    if (!board || !pendingColumnMove) return;

    try {
      // Update column orders for the already visually moved columns
      const updatedBoard = { ...board };
      updatedBoard.columns.forEach((column, index) => {
        column.order = index;
      });
      
      updatedBoard.updatedAt = new Date();
      
      const savedBoard = await kanbanService.updateBoard(board.id, updatedBoard);
      setBoard(savedBoard);
    } catch (error) {
      console.error('Failed to move column:', error);
      // If save fails, revert to original position
      await handleCancelColumnMove();
    } finally {
      setIsColumnMoveConfirmOpen(false);
      setPendingColumnMove(null);
    }
  };

  const handleCancelColumnMove = async () => {
    if (!pendingColumnMove) return;

    try {
      // Revert to original position by reloading from service
      const originalBoard = await kanbanService.getBoard(board?.id || '');
      if (originalBoard) {
        setBoard(originalBoard);
      }
    } catch (error) {
      console.error('Failed to revert column move:', error);
    } finally {
      setIsColumnMoveConfirmOpen(false);
      setPendingColumnMove(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <Spinner size="large" />
          <Text size={500} weight="semibold" className={styles.loadingTitle}>
            Loading Board
          </Text>
          <Text size={300} className={styles.loadingSubtitle}>
            Fetching your tasks and settings...
          </Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <Text size={500} weight="semibold" className={styles.loadingTitle}>
            Failed to Load Board
          </Text>
          <Text size={300} className={styles.loadingSubtitle}>
            {error}
          </Text>
          <Button appearance="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <Spinner size="large" />
          <Text size={500} weight="semibold" className={styles.loadingTitle}>
            Setting Up Board
          </Text>
          <Text size={300} className={styles.loadingSubtitle}>
            Creating your workspace...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', minWidth: 0, maxWidth: '100vw' }}>
      {/* Read-only banner */}
      <ReadOnlyBanner isReadOnly={isReadOnly} />

      {/* License limit banner - shows when on free plan or expired license */}
      <LicenseLimitBanner
        license={license}
        taskCount={board?.cards?.length || 0}
        isReadOnly={isReadOnly}
        onUpgradeClick={openPricingPage}
      />

      {/* Admin consent warning */}
      {memberPermissionWarning && (
        <div style={{
          backgroundColor: tokens.colorPaletteYellowBackground2,
          padding: '0.5rem 2rem',
          borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground1 }}>
            Unable to load all team members. Ask your Teams admin to approve Kanban Project Tracker for full access.
          </Text>
          <Button
            appearance="subtle"
            size="small"
            onClick={() => setMemberPermissionWarning(false)}
            style={{ minWidth: 'auto', padding: '2px 6px' }}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Board Selector Header - hidden in read-only mode */}
      <BoardSelector
        board={board}
        boardList={boardList}
        isReadOnly={isReadOnly}
        onSwitchBoard={handleSwitchBoard}
        onCreateBoard={() => setIsCreateBoardDialogOpen(true)}
        onRenameBoard={handleOpenRenameDialog}
        onDeleteBoard={() => setIsDeleteBoardDialogOpen(true)}
      />

      {/* Board Actions Toolbar */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '0',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground2,
        padding: '0.75rem 2rem',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`
      }}>
        {/* Filters Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="subtle" icon={<Filter24Regular />}>
                Filters
                {(() => {
                  const activeFilters = selectedUserFilters.size + selectedPriorityFilters.size + selectedColorFilters.size + (showPastDueOnly ? 1 : 0);
                  return activeFilters > 0 ? ` (${activeFilters})` : '';
                })()}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <div style={{ padding: '1rem', minWidth: '300px', maxWidth: '400px' }}>
                {/* User Filter */}
                <div style={{ marginBottom: '1rem' }}>
                  <Text size={300} weight="semibold" style={{ display: 'block', marginBottom: '0.5rem' }}>Filter by User</Text>
                  {(() => {
                    const usersWithCards = board.members.filter(user =>
                      board.cards.some(card => card.assignedTo?.some(assignee => assignee.id === user.id))
                    );

                    return (
                      <Combobox
                        multiselect
                        placeholder={
                          usersWithCards.length === 0
                            ? "No users assigned"
                            : selectedUserFilters.size === 0
                              ? "All users"
                              : `${selectedUserFilters.size} user(s) selected`
                        }
                        selectedOptions={Array.from(selectedUserFilters)}
                        onOptionSelect={(_, data) => {
                          if (data.selectedOptions) {
                            setSelectedUserFilters(new Set(data.selectedOptions));
                          }
                        }}
                        listbox={{ style: { maxHeight: '200px' } }}
                        disabled={usersWithCards.length === 0}
                      >
                        {usersWithCards.map(user => (
                          <Option key={user.id} value={user.id} text={user.displayName}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Avatar
                                name={user.displayName}
                                image={userPhotos[user.id] ? { src: userPhotos[user.id] } : undefined}
                                color={getAvatarColor(user.id)}
                                size={24}
                              />
                              {user.displayName}
                            </div>
                          </Option>
                        ))}
                      </Combobox>
                    );
                  })()}
                </div>

                {/* Priority Filter */}
                <div style={{ marginBottom: '1rem' }}>
                  <Text size={300} weight="semibold" style={{ display: 'block', marginBottom: '0.5rem' }}>Filter by Priority</Text>
                  <Combobox
                    multiselect
                    placeholder={
                      selectedPriorityFilters.size === 0
                        ? "All priorities"
                        : `${selectedPriorityFilters.size} priority(s)`
                    }
                    selectedOptions={Array.from(selectedPriorityFilters)}
                    onOptionSelect={(_, data) => {
                      if (data.selectedOptions) {
                        setSelectedPriorityFilters(new Set(data.selectedOptions));
                      }
                    }}
                  >
                    <Option value="high" text="High">High</Option>
                    <Option value="medium" text="Medium">Medium</Option>
                    <Option value="low" text="Low">Low</Option>
                  </Combobox>
                </div>

                {/* Color Filter */}
                <div style={{ marginBottom: '1rem' }}>
                  <Text size={300} weight="semibold" style={{ display: 'block', marginBottom: '0.5rem' }}>Filter by Color</Text>
                  <Combobox
                    multiselect
                    placeholder={
                      selectedColorFilters.size === 0
                        ? "All colors"
                        : `${selectedColorFilters.size} color(s)`
                    }
                    selectedOptions={Array.from(selectedColorFilters)}
                    onOptionSelect={(_, data) => {
                      if (data.selectedOptions) {
                        setSelectedColorFilters(new Set(data.selectedOptions));
                      }
                    }}
                  >
                    {cardColors.filter(color => color.value !== '').map(color => (
                      <Option key={color.value} value={color.value} text={color.name}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ width: '16px', height: '16px', backgroundColor: color.value, borderRadius: '3px' }} />
                          {color.name}
                        </div>
                      </Option>
                    ))}
                  </Combobox>
                </div>

                {/* Past Due Filter */}
                <div style={{ marginBottom: '0.5rem' }}>
                  <Checkbox
                    label="Past due only"
                    checked={showPastDueOnly}
                    onChange={(_, data) => setShowPastDueOnly(data.checked === true)}
                  />
                </div>

                {/* Clear All Filters */}
                {(selectedUserFilters.size > 0 || selectedPriorityFilters.size > 0 || selectedColorFilters.size > 0 || showPastDueOnly) && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
                    <Button
                      appearance="subtle"
                      onClick={() => {
                        setSelectedUserFilters(new Set());
                        setSelectedPriorityFilters(new Set());
                        setSelectedColorFilters(new Set());
                        setShowPastDueOnly(false);
                      }}
                      style={{ width: '100%' }}
                    >
                      Clear All Filters
                    </Button>
                  </div>
                )}
              </div>
            </MenuPopover>
          </Menu>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {!isReadOnly && (
            <>
              <Button
                icon={<Add24Regular />}
                appearance="subtle"
                size="small"
                onClick={() => setIsAddColumnDialogOpen(true)}
              >
                Add Column
              </Button>
              <Button
                icon={<Add24Regular />}
                appearance="subtle"
                size="small"
                onClick={() => setIsCreateRowDialogOpen(true)}
              >
                Add Row
              </Button>
            </>
          )}
          {/* Link Copied Feedback */}
          {shareSuccess && (
            <Text size={200} style={{ color: tokens.colorPaletteGreenForeground1 }}>
              Link Copied!
            </Text>
          )}
          {/* Settings Menu */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                icon={<Settings24Regular />}
                appearance="subtle"
                size="small"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {!isReadOnly && (
                  <MenuItem
                    icon={<Archive24Regular />}
                    onClick={handleOpenArchiveDialog}
                  >
                    Archive
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Share24Regular />}
                  onClick={handleShareBoard}
                >
                  Share
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>
      
      <DragDropContext onDragEnd={handleDragEnd} enableDefaultSensors={!isMobile}>
        <div className={`${styles.board} ${styles.themedScrollbar}`}>
          {/* Integrated Header and Content */}
          <div className={styles.swimlaneContainer}>
            {/* Column Headers Row */}
            <div style={{ marginBottom: '0.25rem' }}>
              <Droppable droppableId="column-headers" direction="horizontal" type="column" isDropDisabled={isMobile}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={styles.columnCells}
                  >
                    {board.columns.map((column, index) => (
                      <Draggable key={column.id} draggableId={column.id} index={index} isDragDisabled={isReadOnly || isMobile}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={styles.columnHeaderCell}
                            style={{
                              ...provided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.8 : 1,
                            }}
                          >
                            <div className={styles.columnHeaderContent}>
                              <div
                                {...(isMobile ? {} : provided.dragHandleProps)}
                                style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: isReadOnly || isMobile ? 'default' : (editingColumn === column.id ? 'text' : 'grab') }}
                              >
                                {editingColumn === column.id ? (
                                  <Input
                                    value={editingColumnTitle}
                                    onChange={(_, data) => setEditingColumnTitle(data.value)}
                                    onKeyDown={handleColumnTitleKeyPress}
                                    onBlur={handleColumnTitleSubmit}
                                    autoFocus
                                    size="large"
                                    style={{ flex: 1 }}
                                  />
                                ) : (
                                  <Text
                                    weight="semibold"
                                    size={500}
                                    style={{ flex: 1, color: tokens.colorNeutralForeground1 }}
                                  >
                                    {column.title}
                                  </Text>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                                {!isReadOnly && (
                                  <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                      <Button
                                        icon={<MoreHorizontal24Regular />}
                                        size="small"
                                        appearance="subtle"
                                      />
                                    </MenuTrigger>
                                    <MenuPopover>
                                      <MenuList>
                                        <MenuItem
                                          icon={<Edit24Regular />}
                                          onClick={() => startEditingColumn(column.id, column.title)}
                                        >
                                          Rename column
                                        </MenuItem>
                                        <MenuItem
                                          icon={<Archive24Regular />}
                                          onClick={() => handleArchiveColumnCards(column.id)}
                                        >
                                          Archive tasks
                                        </MenuItem>
                                        <MenuItem
                                          icon={<Delete24Regular />}
                                          onClick={() => openDeleteColumnCardsConfirm(column.id)}
                                        >
                                          Delete tasks
                                        </MenuItem>
                                        <MenuItem
                                          icon={<Delete24Regular />}
                                          onClick={() => startDeleteColumn(column.id)}
                                        >
                                          Delete column
                                        </MenuItem>
                                      </MenuList>
                                    </MenuPopover>
                                  </Menu>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            {/* Data Rows */}
            {board.rows.map((row) => {
              const isCollapsed = collapsedRows.has(row.id);
              const showRowHeader = board.rows.length > 1;
              return (
                <div key={row.id} className={styles.swimlane}>
                  {/* Row Header with Absolute Positioned Separator - Hidden if only one row */}
                  {showRowHeader && (
                    <div className={styles.rowHeader} style={{ position: 'relative' }}>
                      <Button
                        icon={isCollapsed ? <ChevronRight24Regular /> : <ChevronDown24Regular />}
                        size="small"
                        appearance="subtle"
                        onClick={() => toggleRowCollapsed(row.id)}
                        className={styles.collapseButton}
                      />
                      {editingRow === row.id ? (
                        <Input
                          value={editingRowName}
                          onChange={(_, data) => setEditingRowName(data.value)}
                          onKeyDown={handleRowNameKeyPress}
                          onBlur={handleRowNameSubmit}
                          autoFocus
                          size="medium"
                          style={{ maxWidth: '300px' }}
                        />
                      ) : (
                        <div 
                          className={styles.rowTitle}
                          onClick={() => toggleRowCollapsed(row.id)}
                        >
                          {row.name}
                        </div>
                      )}
                      {!isReadOnly && (
                        <Menu>
                          <MenuTrigger disableButtonEnhancement>
                            <Button
                              icon={<MoreHorizontal24Regular />}
                              size="small"
                              appearance="subtle"
                            />
                          </MenuTrigger>
                          <MenuPopover>
                            <MenuList>
                              <MenuItem
                                icon={<Edit24Regular />}
                                onClick={() => startEditingRow(row.id, row.name)}
                              >
                                Rename row
                              </MenuItem>
                              <MenuItem
                                icon={<Archive24Regular />}
                                onClick={() => handleArchiveRowCards(row.id)}
                              >
                                Archive tasks
                              </MenuItem>
                              <MenuItem
                                icon={<Delete24Regular />}
                                onClick={() => openDeleteRowCardsConfirm(row.id)}
                              >
                                Delete tasks
                              </MenuItem>
                              <MenuItem
                                icon={<Delete24Regular />}
                                onClick={() => startDeleteRow(row.id)}
                              >
                                Delete row
                              </MenuItem>
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      )}
                      {/* Separator positioned absolutely to extend to column end */}
                      <div 
                        className={styles.rowSeparator}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: editingRow === row.id ? '360px' : '120px',
                          width: `${16 + board.columns.length * 280 + (board.columns.length - 1) * 24}px`,
                          transform: 'translateY(-50%)',
                          zIndex: -1
                        }}
                      ></div>
                    </div>
                  )}
                
                  {/* Column Cells */}
                  {!isCollapsed && (
                    <div className={styles.columnCells}>
                    {board.columns.map((column) => {
                    const droppableId = `column-${column.id}-row-${row.id}`;
                    let cardsInCell = board.cards.filter(
                      card => card.columnId === column.id && card.rowId === row.id && !card.archived
                    ).sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

                    // Apply user filter
                    if (selectedUserFilters.size > 0) {
                      cardsInCell = cardsInCell.filter(card =>
                        card.assignedTo?.some(user => selectedUserFilters.has(user.id))
                      );
                    }

                    // Apply priority filter
                    if (selectedPriorityFilters.size > 0) {
                      cardsInCell = cardsInCell.filter(card =>
                        selectedPriorityFilters.has(card.priority)
                      );
                    }

                    // Apply color filter
                    if (selectedColorFilters.size > 0) {
                      cardsInCell = cardsInCell.filter(card =>
                        card.color && selectedColorFilters.has(card.color)
                      );
                    }

                    // Apply past due filter
                    if (showPastDueOnly) {
                      const now = new Date();
                      cardsInCell = cardsInCell.filter(card =>
                        card.endDate && new Date(card.endDate) < now
                      );
                    }

                    return (
                      <Droppable key={droppableId} droppableId={droppableId} isDropDisabled={isMobile}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={styles.columnCell}
                            style={{
                              backgroundColor: snapshot.isDraggingOver ? tokens.colorNeutralBackground3 : 'transparent',
                            }}
                          >
                            {cardsInCell.map((card, index) => (
                              <Draggable key={card.id} draggableId={card.id} index={index} isDragDisabled={isReadOnly || isMobile}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...(isMobile ? {} : provided.dragHandleProps)}
                                    style={{
                                      ...provided.draggableProps.style,
                                      opacity: snapshot.isDragging ? 0.8 : 1,
                                      cursor: isReadOnly || isMobile ? 'default' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                                    }}
                                  >
                                    <TaskCard
                                      card={card}
                                      isReadOnly={isReadOnly}
                                      isHovered={hoveredCard === card.id}
                                      userPhotos={userPhotos}
                                      getAvatarColor={getAvatarColor}
                                      onCardClick={openEditDialog}
                                      onArchive={handleArchiveCard}
                                      onDelete={openDeleteDialog}
                                      onMouseEnter={() => setHoveredCard(card.id)}
                                      onMouseLeave={() => setHoveredCard(null)}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}

                            {/* Add Task Button in each cell */}
                            {!isReadOnly && (
                              <Button
                                icon={<Add24Regular />}
                                size="small"
                                appearance="subtle"
                                onClick={() => {
                                  // Reset all form state before opening dialog
                                  setNewCard({ title: '', description: '', priority: 'medium', assignedTo: [], color: '#2196F3', startDate: '', endDate: '' });
                                  setSelectedAssignees([]);
                                  setValidationErrors({});
                                  setSelectedColumn(column.id);
                                  setSelectedRowForCard(row.id);
                                  setIsCreateCardDialogOpen(true);
                                }}
                                style={{ marginTop: tokens.spacingVerticalXS }}
                              >
                                Add Task
                              </Button>
                            )}
                          </div>
                        )}
                      </Droppable>
                    );
                  })}
                </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
      </DragDropContext>

      {/* Create Task Dialog */}
      <CreateTaskDialog
        isOpen={isCreateCardDialogOpen}
        onClose={() => {
          setIsCreateCardDialogOpen(false);
          setNewCard({ title: '', description: '', priority: 'medium', assignedTo: [], startDate: '', endDate: '' });
          setSelectedAssignees([]);
          setSelectedColumn('');
          setSelectedRowForCard('');
        }}
        onCreateCard={handleCreateCard}
        newCard={newCard}
        setNewCard={setNewCard}
        selectedAssignees={selectedAssignees}
        setSelectedAssignees={setSelectedAssignees}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
        availableMembers={availableMembers}
        userPhotos={userPhotos}
        getAvatarColor={getAvatarColor}
        onAssigneeSelectionChange={handleAssigneeSelectionChange}
        onRemoveAssignee={removeAssignee}
        customUserInput={customUserInput}
        setCustomUserInput={setCustomUserInput}
        onAddCustomUser={addCustomUser}
        themedScrollbarClass={styles.themedScrollbar}
      />

      {/* Edit Task Dialog */}
      <EditTaskDialog
        isOpen={isEditCardDialogOpen}
        onClose={closeEditDialog}
        isReadOnly={isReadOnly}
        editingCard={editingCard}
        setEditingCard={setEditingCard}
        newCard={newCard}
        setNewCard={setNewCard}
        selectedAssignees={selectedAssignees}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
        availableMembers={availableMembers}
        userPhotos={userPhotos}
        getAvatarColor={getAvatarColor}
        onAssigneeSelectionChange={handleAssigneeSelectionChange}
        onRemoveAssignee={removeAssignee}
        customUserInput={customUserInput}
        setCustomUserInput={setCustomUserInput}
        onAddCustomUser={addCustomUser}
        autoSaveCard={autoSaveCard}
        themedScrollbarClass={styles.themedScrollbar}
        license={license}
        openPricingPage={openPricingPage}
        attachmentError={attachmentError}
        setAttachmentError={setAttachmentError}
        onFileUpload={handleFileUpload}
        kanbanService={kanbanService}
        newChecklistItem={newChecklistItem}
        setNewChecklistItem={setNewChecklistItem}
        onAddChecklistItem={handleAddChecklistItem}
        onToggleChecklistItem={handleToggleChecklistItem}
        onDeleteChecklistItem={handleDeleteChecklistItem}
        currentUser={currentUser}
        newComment={newComment}
        setNewComment={setNewComment}
        onAddComment={handleAddComment}
        editingCommentId={editingCommentId}
        editingCommentText={editingCommentText}
        setEditingCommentText={setEditingCommentText}
        onEditComment={handleEditComment}
        onSaveEditComment={handleSaveEditComment}
        onCancelEditComment={handleCancelEditComment}
        onDeleteComment={handleDeleteComment}
        newlyAddedCommentId={newlyAddedCommentId}
        onDeleteAttachment={handleDeleteAttachment}
        formatCommentDate={formatCommentDate}
        commentTextareaRef={commentTextareaRef}
        commentsContainerRef={commentsContainerRef}
        commentRefs={commentRefs}
        columns={board?.columns}
        rows={board?.rows}
        onMoveCard={async (columnId, rowId) => {
          if (!board || !editingCard) return;
          try {
            const updatedBoard = await kanbanService.moveCard(board.id, {
              cardId: editingCard.id,
              targetColumnId: columnId,
              targetRowId: rowId,
              targetIndex: 0,
              movedBy: currentUser?.email,
            });
            setBoard(updatedBoard);
            const updatedCard = updatedBoard.cards.find(c => c.id === editingCard.id);
            if (updatedCard) setEditingCard(updatedCard);
          } catch (error) {
            console.error('Failed to move card:', error);
          }
        }}
      />

      {/* Add Column Dialog */}
      <AddColumnDialog
        isOpen={isAddColumnDialogOpen}
        onClose={() => {
          setIsAddColumnDialogOpen(false);
          setNewColumnTitle('');
        }}
        onConfirm={handleAddColumn}
        columnTitle={newColumnTitle}
        setColumnTitle={setNewColumnTitle}
      />

      {/* Delete Column Confirmation Dialog */}
      <DeleteColumnDialog
        isOpen={isDeleteColumnDialogOpen}
        onClose={() => {
          setIsDeleteColumnDialogOpen(false);
          setColumnToDelete(null);
        }}
        onConfirm={handleDeleteColumn}
        columnTitle={board?.columns.find(col => col.id === columnToDelete)?.title || ''}
        activeTaskCount={board?.cards.filter(card => card.columnId === columnToDelete && !card.archived).length || 0}
      />

      {/* Delete Column Error Dialog */}
      <ErrorDialog
        isOpen={isDeleteErrorDialogOpen}
        onClose={() => {
          setIsDeleteErrorDialogOpen(false);
          setDeleteErrorMessage('');
        }}
        title="Cannot Delete Column"
        message={deleteErrorMessage}
      />

      {/* Create Row Dialog */}
      <AddRowDialog
        isOpen={isCreateRowDialogOpen}
        onClose={() => {
          setIsCreateRowDialogOpen(false);
          setNewRowName('');
        }}
        onConfirm={handleCreateRow}
        rowName={newRowName}
        setRowName={setNewRowName}
      />

      {/* Delete Card Confirmation Dialog */}
      <DeleteTaskDialog
        isOpen={isDeleteCardDialogOpen}
        onClose={() => {
          setIsDeleteCardDialogOpen(false);
          setCardToDelete(null);
        }}
        onConfirm={handleDeleteCard}
        taskTitle={cardToDelete?.title || ''}
      />

      {/* Delete Row Confirmation Dialog */}
      <DeleteRowDialog
        isOpen={isDeleteRowDialogOpen}
        onClose={() => {
          setIsDeleteRowDialogOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDeleteRow}
        rowName={board?.rows.find(row => row.id === rowToDelete)?.name || ''}
        activeTaskCount={board?.cards.filter(card => card.rowId === rowToDelete && !card.archived).length || 0}
      />

      {/* Column Move Confirmation Dialog */}
      <ColumnMoveDialog
        isOpen={isColumnMoveConfirmOpen}
        onClose={handleCancelColumnMove}
        onConfirm={handleConfirmColumnMove}
        columnTitle={pendingColumnMove?.columnTitle || ''}
      />

      {/* Archive Confirmation Dialog */}
      <ArchiveConfirmDialog
        isOpen={isArchiveConfirmOpen}
        onClose={() => {
          setIsArchiveConfirmOpen(false);
          setArchiveConfirmData(null);
        }}
        onConfirm={handleConfirmArchive}
        itemType={archiveConfirmData?.type || 'column'}
        itemName={archiveConfirmData?.name || ''}
        taskCount={archiveConfirmData?.count || 0}
      />

      {/* Delete Cards Confirmation Dialog */}
      <DeleteCardsDialog
        isOpen={isDeleteCardsConfirmOpen}
        onClose={() => {
          setIsDeleteCardsConfirmOpen(false);
          setDeleteCardsConfirmData(null);
        }}
        onConfirm={handleConfirmDeleteCards}
        itemType={deleteCardsConfirmData?.type || 'column'}
        itemName={deleteCardsConfirmData?.name || ''}
        taskCount={deleteCardsConfirmData?.count || 0}
      />



      {/* Archived Tasks Dialog */}
      <ArchivedTasksDialog
        isOpen={isArchiveDialogOpen}
        onClose={() => setIsArchiveDialogOpen(false)}
        isReadOnly={isReadOnly}
        archivedCards={archivedCards}
        searchQuery={archiveSearchQuery}
        setSearchQuery={setArchiveSearchQuery}
        getFilteredArchivedCards={getFilteredArchivedCards}
        onUnarchiveCard={handleUnarchiveCard}
        userPhotos={userPhotos}
        getAvatarColor={getAvatarColor}
        themedScrollbarClass={styles.themedScrollbar}
      />

      {/* Create New Board Dialog */}
      <CreateBoardDialog
        isOpen={isCreateBoardDialogOpen}
        onClose={() => {
          setIsCreateBoardDialogOpen(false);
          setNewBoardTitle('');
        }}
        onConfirm={handleCreateBoard}
        boardName={newBoardTitle}
        setBoardName={setNewBoardTitle}
      />

      {/* Rename Board Dialog */}
      <RenameBoardDialog
        isOpen={isRenameBoardDialogOpen}
        onClose={() => {
          setIsRenameBoardDialogOpen(false);
          setEditingBoardTitle('');
        }}
        onConfirm={handleRenameBoard}
        boardName={editingBoardTitle}
        setBoardName={setEditingBoardTitle}
      />

      {/* Delete Board Confirmation Dialog */}
      <DeleteBoardDialog
        isOpen={isDeleteBoardDialogOpen}
        onClose={() => setIsDeleteBoardDialogOpen(false)}
        onConfirm={handleDeleteBoard}
        boardName={board?.title || ''}
        taskCount={board?.cards?.length || 0}
      />
    </div>
  );
};